import React, { useEffect, useState } from "react"
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet"
import { useLinkPreview, getFallbackData } from "../../services/useLinkPreview"
import { SHOW_SOURCES_EVENT } from "./ChatBubble"

export default function SourcesSidebar() {
    const [isOpen, setIsOpen] = useState(false)
    const [citations, setCitations] = useState([])
    const [side, setSide] = useState("bottom")

    useEffect(() => {
        const handleResize = () => {
            setSide(window.innerWidth < 768 ? "bottom" : "right")
        }
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    useEffect(() => {
        function handleShowSources(event) {
            if (event.detail?.citations) {
                setCitations(event.detail.citations)
                setIsOpen(true)
            }
        }

        document.addEventListener(SHOW_SOURCES_EVENT, handleShowSources)
        return () => document.removeEventListener(SHOW_SOURCES_EVENT, handleShowSources)
    }, [])

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side={side}
                className={side === "bottom" ? "h-[80vh] rounded-t-[2.5rem] p-0 overflow-hidden flex flex-col" : "h-full sm:max-w-md p-0 overflow-hidden flex flex-col"}
            >
                <SheetHeader className="p-4 border-b">
                    <SheetTitle className="text-xl font-bold">Sources</SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">
                    <div className="grid gap-4">
                        {citations.map((citation, index) => (
                            <SourceItem key={index} citation={citation} />
                        ))}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    )
}

function SourceItem({ citation }) {
    const { data: linkPreviewData, isLoading } = useLinkPreview(citation.url);
    const linkPreview = linkPreviewData ?? getFallbackData(citation.url);

    return (
        <a
            href={citation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block p-4 bg-muted/30 border border-border/50 rounded-2xl transition-all duration-200 hover:bg-muted/50 hover:border-border hover:-translate-y-0.5"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 bg-background rounded-lg border border-border shadow-sm flex items-center justify-center overflow-hidden">
                    {linkPreview.favicon && (
                        <img
                            src={linkPreview.favicon}
                            alt={linkPreview.domain}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none" }}
                        />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        {linkPreview.domain}
                    </p>
                    <h4 className="text-sm font-medium text-foreground line-clamp-2 leading-snug group-hover:text-primary transition-colors">
                        {linkPreview.title}
                    </h4>
                    {linkPreview.description && (
                        <p className="mt-2 text-xs text-muted-foreground line-clamp-2 leading-normal">
                            {linkPreview.description}
                        </p>
                    )}
                </div>
            </div>
        </a>
    );
}
